/**
 * insurancePolicy.ts — the dossier's read: ONE policy, on ONE contract.
 *
 * IT IS NOT `fetchContractInsurance`, AND THE DIFFERENCE IS THE SUBJECT. That read answers "which
 * year of cover was this contract SET UP on" and binds to the id-earliest junction [D-B17], which is
 * the right question for a setup canvas grading the cover a contract was activated under. This page
 * is opened ON a policy — the id is in the URL — so the junction it wants is the one row carrying
 * this `insurance_id` AND this `contract_id`, and nothing about the order of the others is any
 * business of the query.
 *
 * SO THE ENVELOPE'S ID-EARLIEST HELPER IS NOT USED BELOW, AND NEITHER IS `order(...).limit(1)`.
 * `UNIQUE (insurance_id, contract_id)` means the pair of filters matches at most one row, so
 * `.maybeSingle()` is the honest terminal: absent answers null, and the day two rows somehow shared
 * a pair PostgREST would say so out loud rather than a `limit(1)` silently handing back whichever
 * one the plan produced first. On a chain of five years that limit is not a tiebreaker, it is a coin
 * toss between four wrong answers and the right one.
 *
 * THE COLUMN LIST IS THE ENVELOPE'S, IMPORTED. `INSURANCE_SELECT` is exported for exactly this
 * reason: two reads of the same rows asking for different columns is the one difference no caller
 * could see from its own file, and the flattening mismatch it produces is what once sent
 * `insurance_contract_id: undefined` into a NOT NULL column.
 *
 * AND THE EMBEDDED ORDERS ARE THE ENVELOPE'S PLUS ONE. Every consumer of this read takes the FIRST
 * of each embedded list, so each list has to say which row that is — and the deposits are ordered
 * here, where the envelope leaves them unordered, because THIS read binds `[0]` of them to an
 * editable link while the envelope only ever sums them [D-B45].
 *
 * THE CHAIN IS A SEPARATE HEAD COUNT, AND THAT IS NOT A REGRESSION. The envelope gets its
 * `chainLength` free from `{ count: 'exact' }` on a list read; a `maybeSingle()` of ONE junction
 * counts one, whatever the contract carries. So the count is asked as its own headless request
 * against `contract_id` alone — no rows transferred, its own cache key, and it moves only when a
 * junction is added or removed rather than on every edit of this year's figures.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

import { INSURANCE_SELECT, type ContractInsuranceLoan } from '../../data/insurance';

/**
 * The key this page's policy read lives under.
 *
 * IT NESTS UNDER `CONTRACT_INSURANCE_KEY` ON PURPOSE. Every writer in this app invalidates the
 * prefix `['contract-insurance']` — the setup canvas and this dossier — so a policy read
 * keyed outside that family would be the one surface that went on showing a year somebody else had
 * just corrected.
 */
export const contractPolicyKey = (contractId: number, policyId: number) =>
    ['contract-insurance', contractId, 'policy', policyId] as const;

/** The same family, for the count of junctions the contract carries [D-B17]. */
export const contractPolicyChainKey = (contractId: number) =>
    ['contract-insurance', contractId, 'chain'] as const;

/** The credit the insurer returned, as it hangs off the premium. */
export type ContractPolicyDiscount = Pick<
    Tables<'insurance_premium_discounts'>,
    'id' | 'transaction_id'
> & {
    transactions: Pick<Tables<'transactions'>, 'id' | 'amount' | 'transaction_at'> | null;
};

/** The payment made to the insurer, with the transaction that IS the money. */
export type ContractPolicyPremium = Pick<Tables<'insurance_premiums'>, 'id' | 'transaction_id'> & {
    transactions: Tables<'transactions'> | null;
    insurance_premium_discounts: ContractPolicyDiscount[];
};

/**
 * The `insurances` row itself, TYPED FROM THE GENERATED SCHEMA rather than restated.
 *
 * The envelope's own `policy` field is an `any`, which is how a column this table has never had
 * came to be read for weeks in silence. Deriving the row from `Tables<'insurances'>` means the next
 * rename fails the typecheck instead of the page.
 */
export type ContractPolicyRow = Tables<'insurances'> & {
    insurance_premiums: ContractPolicyPremium[];
};

export interface ContractPolicy {
    /** `insurance_contracts.id` — what `insurance_loans.insurance_contract_id` points at. */
    junctionId: number;
    /** The `insurances` row. `.id` is the policy id the delete RPC takes. */
    policy: ContractPolicyRow;
    /** This junction's loans, each with its instalments and its deposit links. */
    loans: ContractInsuranceLoan[];
}

/** The junction row as PostgREST hands it back — the select is a string, so the shape is stated here. */
interface JunctionRow {
    id: number;
    insurances: ContractPolicyRow | null;
    insurance_loans: ContractInsuranceLoan[] | null;
}

/**
 * The junction this contract holds on this policy, or null where there is none.
 *
 * NULL IS A REAL ANSWER AND A PAGE STATE: an id that names a policy this contract is not on is the
 * "This contract is not on that policy." screen, with no canvases mounted and every commit refused.
 * It is not an error, because a hand-edited URL is not a failure of anything.
 */
export async function fetchContractPolicy(
    contractId: number,
    policyId: number,
): Promise<ContractPolicy | null> {
    if (!contractId || !policyId) return null;

    const { data, error } = await supabase
        .from('insurance_contracts')
        .select(INSURANCE_SELECT)
        /*
         * BOTH FILTERS, AND THE PAIR IS THE IDENTITY. 14 policies are shared between contracts, so
         * `insurance_id` alone names a row on somebody else's contract; `contract_id` alone names
         * whichever year of the chain the plan felt like returning. Neither filter is optional and
         * neither is a narrowing of the other.
         */
        .eq('insurance_id', policyId)
        .eq('contract_id', contractId)
        /*
         * WITHIN THE JUNCTION, THE ORDER THE SERVER READS IN — the envelope's three, copied
         * character for character because the server's own arms are spelled that way
         * (`ORDER BY il.id LIMIT 1`, `ORDER BY ip.id LIMIT 1`), plus the deposits'.
         *
         * THE DEPOSITS ARE ORDERED HERE AND NOT THERE, AND THE TWO RULES AGREE. The envelope's
         * consumers only ever SUM the deposits, which is order-free; this read binds `[0]` of them
         * to the one editable link on the advance canvas, so it has to say WHICH row that is or the
         * canvas edits a different deposit on different renders.
         */
        .order('id', { referencedTable: 'insurance_loans', ascending: true })
        .order('id', {
            referencedTable: 'insurance_loans.insurance_loan_instalments',
            ascending: true,
        })
        .order('id', {
            referencedTable: 'insurance_loans.insurance_loan_deposits',
            ascending: true,
        })
        .order('id', { referencedTable: 'insurances.insurance_premiums', ascending: true })
        .maybeSingle();

    if (error) throw error;

    const junction = data as unknown as JunctionRow | null;
    if (!junction || !junction.insurances) return null;

    return {
        junctionId: junction.id,
        policy: junction.insurances,
        loans: (junction.insurance_loans ?? []) as ContractInsuranceLoan[],
    };
}

/**
 * How many junctions this contract has in all — 1 is the ordinary contract, >1 is a renewal chain.
 *
 * A HEAD COUNT: `{ count: 'exact', head: true }` asks PostgREST for the `Content-Range` total and
 * no rows at all, which is the whole of the question. IT GATES NOTHING [D-B17] — the owner ruled on
 * 2026-08-05 that a chained policy stays editable — so this is a line the Policy canvas states and
 * never a lock.
 *
 * A COUNT THE TRANSPORT DROPPED READS AS ONE, never as none: the page is open on a junction, so the
 * chain is at least this year.
 */
export async function fetchPolicyChainLength(contractId: number): Promise<number> {
    if (!contractId) return 1;
    const { count, error } = await supabase
        .from('insurance_contracts')
        .select('id', { count: 'exact', head: true })
        .eq('contract_id', contractId);
    if (error) throw error;
    return count ?? 1;
}
